import { useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { DocumentPageListItem } from '@avisoma/shared';
import backend from '../services/backend';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  DocumentIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../catalyst/button';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../catalyst/dropdown';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';
import DocumentPages, { type DocumentPagesRef } from '../components/DocumentPages';
import { DocumentReconstructionPreview } from '../components/DocumentReconstructionPreview';
import { PageHeader } from '../components/PageHeader';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DebugObject } from '../components/DebugObject';
import { entityViewTitle } from '../components/ID';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { EngineEvents } from '../components/EngineEvents';
import { LLPEvents } from '../components/LLPEvents';
import { DocumentProjects } from '../components/DocumentProjects';
import {
  documentRefetchInterval,
  makePagesRefetchInterval,
  makeStrandedRefetchInterval,
} from './documentView/useDocumentAutoRefresh';
import { DeleteConfirmDialog } from './documentView/DeleteConfirmDialog';
import { DocumentInfoCard } from './documentView/DocumentInfoCard';
import { LineageMap } from './documentView/LineageMap';
import { InvalidFileAlert } from './documentView/InvalidFileAlert';
import { ProcessingStatusCard } from './documentView/ProcessingStatusCard';
import { ClassificationStatsPanel } from './documentView/ClassificationStatsPanel';
import { HeavyVisualGate } from './documentView/HeavyVisualGate';
import {
  getReconstructionVisualState,
  reconstructedDocumentsPreviewInstanceKey,
  runReconstructionVisualRetry,
} from './documentView/reconstructedDocumentsPagination';
import { useDocumentReconstructedDocuments } from './documentView/useDocumentReconstructedDocuments';
import {
  isHugeClassificationVisual as getIsHugeClassificationVisual,
  type ClassificationPageMetric,
} from '../lib/classificationVisuals';

export type { ClassificationPageMetric } from '../lib/classificationVisuals';

export type DocumentPageStatusesAggregate = {
  totalPages: number;
  metadata: Record<string, number>;
  pdfParser: Record<string, number>;
  embeddings: Record<string, number>;
  classification: Record<string, number>;
};

export function buildClassificationPageMetrics(pages: DocumentPageListItem[] | undefined): ClassificationPageMetric[] {
  if (!pages?.length) return [];
  return pages.map((page, index) => {
    const result = page.processing?.classification?.result;
    const top1 = result?.[0]?.[1] ?? 0;
    const top2 = result?.[1]?.[1] ?? 0;
    return {
      pageId: page.id,
      pageNumber: index + 1,
      confidence: top1,
      margin: result?.length && result.length >= 2 ? top1 - top2 : top1,
      top1Category: result?.[0]?.[0],
    };
  });
}

export function DocumentContent({
  id,
  embedded = false,
  onClose,
}: {
  id: string;
  embedded?: boolean;
  onClose?: () => void;
}) {
  useDocumentTitle(embedded ? undefined : entityViewTitle(id, 'Document View'));
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const auth = useAuth();
  const isAdmin = auth.hasAccess(['admin']);
  const toast = useToast();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const documentPagesRef = useRef<DocumentPagesRef>(null);

  const documentQuery = useQuery({
    queryKey: ['document', id],
    queryFn: () => {
      if (!id) throw new Error('No document ID provided');
      return backend.getDocument(id).then((res) => res.data);
    },
    refetchInterval: documentRefetchInterval,
  });

  const pagesQuery = useQuery<DocumentPageListItem[]>({
    queryKey: ['documentPages', id],
    queryFn: async () => {
      if (!id) throw new Error('No document ID provided');
      return backend.getDocumentPages(id).then((res) => res.data.items);
    },
    refetchInterval: makePagesRefetchInterval(queryClient, id),
  });

  // Document detail carries only the reconstruction count. The cursor controller below owns
  // every per-entry consumer, including the preview and reconstruction geometry.
  const reconstruction = documentQuery.data?.processing?.reconstruction;

  const strandedQuery = useQuery({
    queryKey: ['stranded', id],
    queryFn: () => {
      if (!id) throw new Error('No document ID provided');
      return backend.getStrandedPages(id).then((res) => res.data);
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: makeStrandedRefetchInterval(queryClient, id),
  });

  // pageId -> stranded stage, consumed by DocumentPages (Task 8) to badge rows.
  const strandedByPage = useMemo(() => {
    const map = new Map<string, string>();
    (strandedQuery.data?.pages ?? []).forEach((p) => map.set(p.pageId, p.stage));
    return map;
  }, [strandedQuery.data]);

  // Segmentation breakdown for the info card: segment children carry a
  // parentPageId, so distinct parents = pages that were split and the child
  // count = pages added by segmentation. Original page count = active leaves
  // minus the added children plus the (swapped-out) parents.
  const segmentationSummary = useMemo(() => {
    const pages = pagesQuery.data;
    const activeLeaves = documentQuery.data?.processing?.pages?.pageCount ?? pages?.length ?? 0;
    const children = pages?.filter((p) => p.parentPageId) ?? [];
    const childCount = children.length;
    const parentCount = new Set(children.map((p) => p.parentPageId)).size;
    return { originalPages: activeLeaves - childCount + parentCount, parentCount, childCount };
  }, [pagesQuery.data, documentQuery.data]);

  const classificationMetrics = useMemo(
    () => buildClassificationPageMetrics(pagesQuery.data),
    [pagesQuery.data],
  );
  const isPageWorkloadKnown = pagesQuery.data !== undefined;
  const isHugeClassificationVisual = getIsHugeClassificationVisual(classificationMetrics.length);
  const reconstructedDocumentsQuery = useDocumentReconstructedDocuments({
    documentId: id,
    reconstructionStatus: reconstruction?.status,
    reconstructionCompletedAt: reconstruction?.completedAt,
    reconstruction: reconstruction?.result,
    isPageWorkloadKnown,
    isHugeClassificationVisual,
    hasSegmentedLineage: segmentationSummary.parentCount > 0,
    pageCount: pagesQuery.data?.length ?? 0,
  });
  const pageWorkloadError = pagesQuery.error instanceof Error ? pagesQuery.error : undefined;
  const reconstructionVisualState = getReconstructionVisualState({
    isPageWorkloadKnown,
    isPageWorkloadFetching: pagesQuery.isFetching,
    pageWorkloadError,
    isGeometryLoading: reconstructedDocumentsQuery.isVisualLoading,
    geometryError: reconstructedDocumentsQuery.error,
  });
  const retryReconstructionVisuals = () => {
    runReconstructionVisualRetry({
      requestVisuals: reconstructedDocumentsQuery.requestVisuals,
      pageWorkloadError,
      refetchPages: pagesQuery.refetch,
      restartGeometry: reconstructedDocumentsQuery.retry,
    });
  };

  const handlePageNumberClick = (pageNumber: number) => {
    const pageIndex = pageNumber - 1;
    if (pageIndex >= 0) {
      documentPagesRef.current?.scrollToPage(pageIndex);
    }
  };

  const deleteDocumentMutation = useMutation({
    mutationFn: (documentId: string) => backend.deleteDocument(documentId),
    onSuccess: ({ data }) => {
      const pending = data.purge?.pending ?? 0;
      if (pending > 0) {
        toast.info(
          `Document deleted. ${pending} storage object${pending === 1 ? ' is' : 's are'} still being cleaned up and will be retried automatically.`,
        );
      }
      if (embedded) {
        onClose?.();
      } else {
        navigate('/documents');
      }
    },
  });

  const handleDelete = async () => {
    if (!documentQuery.data) return;
    deleteDocumentMutation.mutate(documentQuery.data.id);
  };

  if (documentQuery.isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-zinc-900 py-6 flex flex-col justify-center sm:py-12">
        <div className="text-center">
          <div className="text-xl text-gray-600 dark:text-zinc-400">Loading document...</div>
        </div>
      </div>
    );
  }

  if (documentQuery.error || !documentQuery.data) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-zinc-900 py-6 flex flex-col justify-center sm:py-12">
        <div className="text-center">
          <div className="text-xl text-red-600 dark:text-red-400">
            {documentQuery.error?.message ?? 'Document not found'}
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-500 dark:bg-blue-400 text-white rounded hover:bg-blue-600 dark:hover:bg-blue-500"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  const isInvalid = documentQuery.data.status === 'invalid';
  // DR-2444: conversion failures (e.g. Gotenberg on an encrypted file) land on document
  // status 'error' with an errored pages stage — surface the same alert as invalid files.
  const pageSplitErrored =
    documentQuery.data.status === 'error' && documentQuery.data.processing?.pages?.status === 'error';

  return (
    <div className="">
      <PageHeader
        title={entityViewTitle(id, documentQuery.data.fileName)}
        description="View and manage document details"
        icon={<DocumentIcon className="size-6" />}
        showBackButton={!embedded}
      >
        <div className="flex items-center gap-2 whitespace-nowrap">
          <DebugObject object={documentQuery.data} />

          <Dropdown>
            <DropdownButton as={Button} outline disabled={deleteDocumentMutation.isPending}>
              {deleteDocumentMutation.isPending ? (
                <>
                  <ArrowPathIcon className="animate-spin" />
                  Deleting…
                </>
              ) : (
                <>Actions</>
              )}
              <ChevronDownIcon />
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
              <DropdownItem
                href={`/api/documents/${documentQuery.data.id}/download`}
                download={documentQuery.data.fileName}
              >
                <ArrowDownTrayIcon />
                <DropdownLabel>Download</DropdownLabel>
              </DropdownItem>
              {isAdmin && (
                <>
                  <DropdownDivider />
                  <DropdownItem onClick={() => setShowDeleteConfirm(true)}>
                    <TrashIcon className="text-red-600 dark:text-red-400" />
                    <DropdownLabel className="text-red-600 dark:text-red-400">Delete Document</DropdownLabel>
                  </DropdownItem>
                </>
              )}
            </DropdownMenu>
          </Dropdown>
        </div>
      </PageHeader>

      <DeleteConfirmDialog
        documentId={documentQuery.data.id}
        open={showDeleteConfirm}
        isPending={deleteDocumentMutation.isPending}
        onConfirm={handleDelete}
        onClose={() => setShowDeleteConfirm(false)}
      />

      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <DocumentInfoCard
              document={documentQuery.data}
              onTagsSaved={(updated) => queryClient.setQueryData(['document', id], updated)}
              segmentation={segmentationSummary}
            />

            {(isInvalid || pageSplitErrored) && (
              <InvalidFileAlert
                invalidReason={documentQuery.data.processing?.pages?.invalidReason}
                errorMessage={documentQuery.data.processing?.pages?.errorMessage}
              />
            )}

            <ProcessingStatusCard
              document={documentQuery.data}
              queryId={id}
              strandedCount={strandedQuery.data?.count ?? 0}
            />
          </div>

          <ClassificationStatsPanel
            document={documentQuery.data}
            pages={pagesQuery.data}
            classificationMetrics={classificationMetrics}
            isHugeClassificationVisual={isHugeClassificationVisual}
            reconstructedDocuments={reconstructedDocumentsQuery.items}
            reconstructionVisualsLoading={reconstructionVisualState.loading}
            reconstructionVisualsError={reconstructionVisualState.error}
            onRequestReconstructionVisuals={reconstructedDocumentsQuery.requestVisuals}
            onRetryReconstructionVisuals={retryReconstructionVisuals}
            onPageNumberClick={handlePageNumberClick}
            onChartPageClick={(page) => {
              const pageIndex = pagesQuery.data?.findIndex((p) => p.id === page.id) ?? -1;
              if (pageIndex !== -1) {
                documentPagesRef.current?.scrollToPage(pageIndex);
              }
            }}
          />
        </div>
      </div>

      {reconstructedDocumentsQuery.showLineage && (
        <div className="mb-6">
          <HeavyVisualGate
            isHuge={reconstructedDocumentsQuery.isHugeLineageVisual}
            label="the segmentation & reconstruction map"
            loading={reconstructionVisualState.loading}
            error={reconstructionVisualState.error}
            onRequestRender={reconstructedDocumentsQuery.requestVisuals}
            onRetry={retryReconstructionVisuals}
          >
            <LineageMap pages={pagesQuery.data ?? []} entities={reconstructedDocumentsQuery.items} />
          </HeavyVisualGate>
        </div>
      )}

      <DocumentPages
        ref={documentPagesRef}
        document={documentQuery.data}
        pagesQuery={pagesQuery}
        strandedByPage={strandedByPage}
        requestRelad={() => pagesQuery.refetch()}
      />

      <DocumentReconstructionPreview
        key={reconstructedDocumentsPreviewInstanceKey({
          documentId: documentQuery.data.id,
          reconstructionStatus: reconstruction?.status,
          reconstructionCompletedAt: reconstruction?.completedAt,
          recoveryVersion: reconstructedDocumentsQuery.recoveryVersion,
        })}
        documentId={documentQuery.data.id}
        fileName={documentQuery.data.fileName}
        items={reconstructedDocumentsQuery.items}
        totalCount={reconstructedDocumentsQuery.totalCount}
        hasMore={reconstructedDocumentsQuery.hasMore}
        isLoading={reconstructedDocumentsQuery.isLoading}
        isLoadingMore={reconstructedDocumentsQuery.isLoadingMore}
        error={reconstructedDocumentsQuery.error}
        isGenerationConflict={reconstructedDocumentsQuery.isGenerationConflict}
        onLoadMore={reconstructedDocumentsQuery.loadMore}
        onRetry={reconstructedDocumentsQuery.retry}
      />

      {documentQuery.data.processing?.pdfParser?.result?.engineEvents && (
        <EngineEvents events={documentQuery.data.processing.pdfParser.result.engineEvents} />
      )}

      {documentQuery.data.processing?.pdfParser?.result?.llpStatusEvents && (
        <LLPEvents events={documentQuery.data.processing.pdfParser.result.llpStatusEvents} />
      )}

      <DocumentProjects documentId={documentQuery.data.id} />
    </div>
  );
}

export default function DocumentView() {
  const { id } = useParams<{ id: string }>();
  // DR-2426: key by document id so navigating between documents remounts the subtree —
  // otherwise per-document state (HeavyVisualGate opt-ins, the pages window, pending
  // scroll) leaks from one document to the next.
  return <DocumentContent key={id} id={id!} />;
}
